import type { CSSProperties } from 'react';

/**
 * Dark-theme-safe inline styles for the raw (non-MUI) controls scattered across
 * the admin editors. The admin is hardwired to the dark theme, but these inline
 * styles can't read the styled-theming palette (there's no styled-component
 * context), and native form controls don't inherit the theme's text color — so a
 * bare `<button>` renders black-on-dark. These shared constants keep such controls
 * readable and consistent instead of each editor rolling its own colors.
 */

export const darkText = '#dddddd';
export const darkTextDim = '#999999';
export const darkDanger = '#ff6b6b';
export const darkBorder = '#3a3a42';

/** "+ Add …" buttons: dashed outline, transparent fill, light text. */
export const addButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px dashed #6b6b73',
    background: 'transparent',
    color: darkText,
    borderRadius: 4,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
};

/** Icon action buttons (move up/down, edit): transparent, light icon (mdi icons use currentColor). */
export const iconButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: darkText,
    cursor: 'pointer',
    padding: 4,
};

/** Destructive icon/text buttons (remove/delete). */
export const dangerButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    border: 'none',
    background: 'transparent',
    color: darkDanger,
    cursor: 'pointer',
    fontSize: 13,
};

/** Secondary filled action button (Save/Publish) — subtle on the dark surface. */
export const plainButtonStyle: CSSProperties = {
    border: `1px solid ${darkBorder}`,
    background: '#33333b',
    color: darkText,
    borderRadius: 4,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
};

/* -------------------------------------------------------------------------- */
/*                          Feedback banners & statuses                        */
/* -------------------------------------------------------------------------- */

/**
 * The admin is hardwired to the dark theme (surfaces are #232329 / #2a2a31, body
 * text #dddddd — see utils/palette.js). Every editor grew its own feedback banner
 * as a MATERIAL-LIGHT pair — `background: '#ffebee', color: '#c62828'` and friends
 * — copied from editor to editor until there were fourteen of them.
 *
 * Two separate problems, and only one of them is obvious:
 *  · The banners are *legible* (dark text on their own light fill) but they are
 *    bright pink/green rectangles on a dark page — they read as broken chrome,
 *    and at night they glare.
 *  · The same palette used as BARE TEXT with no fill — `color: '#c62828'` on a
 *    #232329 surface — is roughly 2.3:1 contrast. That one is simply unreadable,
 *    and it is what "dark text on the dark theme" actually refers to.
 *
 * These tokens fix both: a translucent tint of the status hue for the fill, and a
 * LIGHTENED hue for the text, so the same semantic colour works as a banner and
 * as bare text. Use them instead of hand-rolling a pair.
 */
const bannerBase: CSSProperties = {
    padding: 10,
    borderRadius: 4,
    marginBottom: 12,
    fontSize: 13,
    lineHeight: 1.5,
};

/**
 * **These four are TEXT colours. Do not use one as a FILL.**
 *
 * They are deliberately LIGHTENED so they read on a #232329 surface, which means
 * they are also light enough that white text on top of them is unreadable —
 * `background: darkSuccessText, color: 'white'` measures about 1.8:1. The
 * Schedules queue shipped exactly that and the status label could not be read, on
 * a column whose entire job is to say what happened. When a status needs a CHIP,
 * use the banner recipe below: a translucent wash of the hue for the fill, the
 * light hue for the label.
 */
/** Light enough to read on #232329; still unmistakably "danger". */
export const darkDangerText = '#ff9b9b';
export const darkSuccessText = '#6ddba0';
export const darkWarningText = '#e0a458';
export const darkInfoText = '#7da3ff';

export const errorBannerStyle: CSSProperties = {
    ...bannerBase,
    background: 'rgba(255, 107, 107, 0.12)',
    border: '1px solid rgba(255, 107, 107, 0.35)',
    color: darkDangerText,
};

export const successBannerStyle: CSSProperties = {
    ...bannerBase,
    background: 'rgba(109, 219, 160, 0.12)',
    border: '1px solid rgba(109, 219, 160, 0.35)',
    color: darkSuccessText,
};

export const warningBannerStyle: CSSProperties = {
    ...bannerBase,
    background: 'rgba(224, 164, 88, 0.12)',
    border: '1px solid rgba(224, 164, 88, 0.35)',
    color: darkWarningText,
};

/** A neutral inset panel (checklists, previews) that belongs on a dark surface. */
export const darkPanelStyle: CSSProperties = {
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${darkBorder}`,
    borderRadius: 8,
    padding: '12px 14px',
};
